import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { snapUnixSecondsToMetaBudgetScheduleUtc } from "@common/utils/campaignBudgetMeta";
import { resolveEventWallTimezone } from "./eventWallMinuteStrings";
import {
  META_PHASE_MIN_GAP_MINUTES,
  addWallMinutesThenSnapWallMinute,
  snapWallMinuteToMetaBudgetUtc,
} from "./metaBudgetScheduleWallSnap";
import { wallMinuteStringToUnixSeconds } from "./wallMinuteUnix";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * When a phase would have `time_end` not strictly after `time_start`, bump `time_end` to
 * `time_start + {@link META_PHASE_MIN_GAP_MINUTES}` (snapped as Meta end). Merges `patch` with
 * `row` only for the time fields used in the comparison.
 */
export function clampScheduledPhaseWallPatchForMinGap(
  row: { time_start: string; time_end: string },
  patch: Partial<{ time_start: string; time_end: string }>,
  eventTimezone: string
): Partial<{ time_start: string; time_end: string }> {
  const nextStart = (patch.time_start ?? row.time_start)?.trim() ?? "";
  const nextEnd = (patch.time_end ?? row.time_end)?.trim() ?? "";
  if (!nextStart || !nextEnd) return patch;
  const tz = resolveEventWallTimezone(eventTimezone);
  const dS = dayjs.tz(nextStart.slice(0, 16), tz);
  const dE = dayjs.tz(nextEnd.slice(0, 16), tz);
  if (!dS.isValid() || !dE.isValid()) return patch;
  if (dE.isAfter(dS)) return patch;
  return {
    ...patch,
    time_end: addWallMinutesThenSnapWallMinute(
      nextStart,
      tz,
      META_PHASE_MIN_GAP_MINUTES,
      "end"
    ),
  };
}

function unixSecondsToSnappedWallMinute(
  unixSeconds: number,
  eventTimezone: string,
  bound: "start" | "end"
): string {
  const tz = resolveEventWallTimezone(eventTimezone);
  const raw = dayjs.unix(unixSeconds).tz(tz).format("YYYY-MM-DDTHH:mm");
  return snapWallMinuteToMetaBudgetUtc(raw, tz, bound);
}

/**
 * Full ad delivery window for a campaign run — when ads may deliver — as UTC ISO instants.
 * Scheduled budget phases must lie entirely inside the delivery window UTC ISO bounds.
 * Same contract as {@link validateScheduledBudgetWithinAdFlight} in `campaignBudgetMeta`.
 */
export type ScheduledBudgetAdDeliveryWindowUtc = {
  /** Delivery window start (UTC ISO), from campaign / ad set run schedule. */
  deliveryWindowStartIso: string;
  /** Delivery window end (UTC ISO). */
  deliveryWindowEndIso: string;
};

/**
 * If merged segment walls fall outside the ad delivery window, nudge `time_start` up /
 * `time_end` down onto Meta’s 15m grid (same unix rules as publish-side validation).
 */
export function clampScheduledPhaseWallPatchToAdFlight(
  row: { time_start: string; time_end: string },
  patch: Partial<{ time_start: string; time_end: string }>,
  eventTimezone: string,
  deliveryWindow: ScheduledBudgetAdDeliveryWindowUtc
): Partial<{ time_start: string; time_end: string }> {
  const tz = resolveEventWallTimezone(eventTimezone);
  const flightStart = dayjs(deliveryWindow.deliveryWindowStartIso.trim());
  const flightEnd = dayjs(deliveryWindow.deliveryWindowEndIso.trim());
  if (!flightStart.isValid() || !flightEnd.isValid()) return patch;
  const t0 = flightStart.unix();
  const t1 = flightEnd.unix();
  if (t1 <= t0) return patch;

  const mergedStart = (patch.time_start ?? row.time_start)?.trim() ?? "";
  const mergedEnd = (patch.time_end ?? row.time_end)?.trim() ?? "";
  const out: Partial<{ time_start: string; time_end: string }> = { ...patch };

  if (mergedStart) {
    const u = wallMinuteStringToUnixSeconds(mergedStart, tz);
    if (u != null && u < t0) {
      const snapped = snapUnixSecondsToMetaBudgetScheduleUtc(t0, "ceil");
      out.time_start = unixSecondsToSnappedWallMinute(snapped, tz, "start");
    }
  }
  if (mergedEnd) {
    const u = wallMinuteStringToUnixSeconds(mergedEnd, tz);
    if (u != null && u > t1) {
      const snapped = snapUnixSecondsToMetaBudgetScheduleUtc(t1, "floor");
      out.time_end = unixSecondsToSnappedWallMinute(snapped, tz, "end");
    }
  }
  return out;
}

/**
 * Applies ad-delivery-window clamping then phase order clamping, repeating until stable.
 */
export function clampScheduledPhaseWallPatch(
  row: { time_start: string; time_end: string },
  patch: Partial<{ time_start: string; time_end: string }>,
  eventTimezone: string,
  deliveryWindow: ScheduledBudgetAdDeliveryWindowUtc | null | undefined
): Partial<{ time_start: string; time_end: string }> {
  if (
    !deliveryWindow?.deliveryWindowStartIso?.trim() ||
    !deliveryWindow?.deliveryWindowEndIso?.trim()
  ) {
    return clampScheduledPhaseWallPatchForMinGap(row, patch, eventTimezone);
  }
  let cur = { ...patch };
  for (let i = 0; i < 10; i++) {
    const before = JSON.stringify(cur);
    cur = clampScheduledPhaseWallPatchToAdFlight(
      row,
      cur,
      eventTimezone,
      deliveryWindow
    );
    cur = clampScheduledPhaseWallPatchForMinGap(row, cur, eventTimezone);
    if (JSON.stringify(cur) === before) break;
  }
  return cur;
}
